import { useState, type FormEvent } from "react";
import { meApi } from "../../api/me";
import type { UserOut } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { Alert, Button, Card, Input } from "../../ui";

/** Two-factor authentication (docs/phase6/P6-T01 §4): set up with a QR code, confirm, keep
 * the recovery codes; disable with a code or the password; regenerate codes. */
export function MfaCard({
  me,
  onChanged,
}: {
  me: UserOut;
  onChanged: () => Promise<unknown> | void;
}) {
  const [setup, setSetup] = useState<{
    secret: string;
    otpauth_uri: string;
    qr_data_url: string;
  } | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
    } catch (err) {
      setMessage({ kind: "error", text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const start = () =>
    run(async () => {
      setSetup(await meApi.mfaSetup());
      setRecoveryCodes(null);
    });
  const confirm = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const result = await meApi.mfaConfirm(code);
      setRecoveryCodes(result.recovery_codes);
      setSetup(null);
      setCode("");
      setMessage({ kind: "success", text: "Two-factor authentication is on." });
      await onChanged();
    });
  };
  const disable = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await meApi.mfaDisable(code ? { code } : { password });
      setCode("");
      setPassword("");
      setRecoveryCodes(null);
      setMessage({
        kind: "success",
        text: "Two-factor authentication is off.",
      });
      await onChanged();
    });
  };
  const regenerate = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const result = await meApi.mfaRecoveryCodes(code);
      setRecoveryCodes(result.recovery_codes);
      setCode("");
      setMessage({
        kind: "success",
        text: "New recovery codes issued; the old ones no longer work.",
      });
    });
  };

  return (
    <Card>
      <h2 className="mb-2 text-xl font-semibold">Two-factor authentication</h2>
      {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
      {recoveryCodes ? (
        <div className="my-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="font-medium">
            Recovery codes — save them now, they are shown once.
          </p>
          <ul
            className="mt-2 grid grid-cols-2 gap-1 font-mono"
            aria-label="Recovery codes"
          >
            {recoveryCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {me.mfa_enabled ? (
        <div className="flex flex-col gap-4 text-sm">
          <p className="text-slate-600">
            Enabled. Sign-ins ask for a code from your authenticator app.
          </p>
          <form
            onSubmit={regenerate}
            className="flex max-w-md items-end gap-2"
            aria-label="New recovery codes"
          >
            <Input
              label="Code from your app"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
            />
            <Button
              type="submit"
              variant="secondary"
              loading={busy}
              disabled={!code}
            >
              New recovery codes
            </Button>
          </form>
          <form
            onSubmit={disable}
            className="flex max-w-md flex-col gap-2"
            aria-label="Disable two-factor"
          >
            <p className="text-slate-600">
              To turn it off, enter a code from your app or a recovery code, or
              your password.
            </p>
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              variant="danger"
              loading={busy}
              disabled={!code && !password}
            >
              Turn off two-factor authentication
            </Button>
          </form>
        </div>
      ) : setup ? (
        <form
          onSubmit={confirm}
          className="flex max-w-md flex-col gap-3 text-sm"
          aria-label="Confirm two-factor"
        >
          <p className="text-slate-600">
            Scan the QR code with your authenticator app (or enter the key by
            hand), then type the six-digit code it shows.
          </p>
          <img
            src={setup.qr_data_url}
            alt="QR code for your authenticator app"
            className="h-40 w-40"
          />
          <code className="break-all text-xs">{setup.secret}</code>
          <Input
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            required
          />
          <div className="flex gap-2">
            <Button type="submit" loading={busy}>
              Confirm
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setSetup(null)}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div className="text-sm">
          <p className="mb-3 text-slate-600">
            Add a second step to every sign-in with an authenticator app (TOTP).
          </p>
          <Button onClick={() => void start()} loading={busy}>
            Set up two-factor authentication
          </Button>
        </div>
      )}
    </Card>
  );
}
